import { useRef, useCallback, useEffect } from "react"
import type { CursorState, InterpolatedCursor } from "./types"

const INTERPOLATION_FACTOR = 0.15 // Smooth factor for lerp
const MAX_INTERPOLATION_DISTANCE = 500 // Snap if cursor jumped too far

export function useCursorInterpolation(
	cursorsRef: React.MutableRefObject<Map<string, InterpolatedCursor>>,
	onUpdate: (cursors: Map<string, InterpolatedCursor>) => void,
) {
	const animationFrameRef = useRef<number | null>(null)
	const lastFrameTimeRef = useRef<number>(0)

	const updateCursor = useCallback(
		(cursor: CursorState) => {
			// Use connectionId as the key for cursor tracking
			const existing = cursorsRef.current.get(cursor.connectionId)

			if (!existing) {
				// New cursor
				const interpolated: InterpolatedCursor = {
					...cursor,
					targetPosition: { ...cursor.position },
					velocity: { x: 0, y: 0 },
					lastUpdateTime: Date.now(),
					targetDraggedNodes: cursor.draggedNodes,
					targetSelectionBox: cursor.selectionBox
						? {
								x: cursor.selectionBox.x,
								y: cursor.selectionBox.y,
								width: cursor.selectionBox.width,
								height: cursor.selectionBox.height,
							}
						: undefined,
				}
				cursorsRef.current.set(cursor.connectionId, interpolated)
			} else {
				// Update target position
				const dx = cursor.position.x - existing.targetPosition.x
				const dy = cursor.position.y - existing.targetPosition.y
				const distance = Math.sqrt(dx * dx + dy * dy)

				// Calculate velocity for prediction
				const dt = cursor.timestamp - existing.timestamp
				const velocity =
					dt > 0 ? { x: (dx / dt) * 1000, y: (dy / dt) * 1000 } : { x: 0, y: 0 }

				// For selectionBox, preserve interpolated x/y/width/height but update mode and selectedNodeIds
				let interpolatedSelectionBox = cursor.selectionBox
					? (existing.selectionBox ?? cursor.selectionBox)
					: undefined
				if (interpolatedSelectionBox && cursor.selectionBox) {
					// Update mode and selectedNodeIds from incoming cursor
					interpolatedSelectionBox = {
						...interpolatedSelectionBox,
						mode: cursor.selectionBox.mode,
						selectedNodeIds: cursor.selectionBox.selectedNodeIds,
					}
				}

				const interpolated: InterpolatedCursor = {
					...cursor,
					position:
						distance > MAX_INTERPOLATION_DISTANCE
							? { ...cursor.position }
							: { ...existing.position },
					targetPosition: { ...cursor.position },
					velocity,
					lastUpdateTime: Date.now(),
					// Preserve current interpolated values, set new targets
					draggedNodes: cursor.draggedNodes
						? (existing.draggedNodes ?? cursor.draggedNodes)
						: undefined,
					targetDraggedNodes: cursor.draggedNodes,
					selectionBox: interpolatedSelectionBox,
					targetSelectionBox: cursor.selectionBox
						? {
								x: cursor.selectionBox.x,
								y: cursor.selectionBox.y,
								width: cursor.selectionBox.width,
								height: cursor.selectionBox.height,
							}
						: undefined,
				}

				cursorsRef.current.set(cursor.connectionId, interpolated)
			}

			// Trigger update for state changes (dragging, connecting, etc.)
			// This ensures draggedNodePosition and connectingFrom are reflected immediately
			onUpdate(new Map(cursorsRef.current))
		},
		[cursorsRef, onUpdate],
	)

	const removeCursor = useCallback(
		(connectionId: string) => {
			cursorsRef.current.delete(connectionId)
			onUpdate(new Map(cursorsRef.current))
		},
		[cursorsRef, onUpdate],
	)

	// Animation loop for smooth interpolation
	useEffect(() => {
		const animate = (time: number) => {
			const deltaTime = time - lastFrameTimeRef.current
			lastFrameTimeRef.current = time

			let hasChanges = false

			for (const [_userId, cursor] of cursorsRef.current) {
				const { position, targetPosition } = cursor

				// Calculate lerp factor based on delta time
				const factor = Math.min(INTERPOLATION_FACTOR * (deltaTime / 16.67), 1)

				// Interpolate cursor position
				const dx = targetPosition.x - position.x
				const dy = targetPosition.y - position.y
				const distance = Math.sqrt(dx * dx + dy * dy)

				if (distance > 0.5) {
					// Lerp towards target
					cursor.position = {
						x: position.x + dx * factor,
						y: position.y + dy * factor,
					}
					hasChanges = true
				} else if (distance > 0) {
					// Snap to target
					cursor.position = { ...targetPosition }
					hasChanges = true
				}

				// Interpolate dragged nodes
				if (cursor.targetDraggedNodes?.length && cursor.draggedNodes?.length) {
					cursor.draggedNodes = cursor.targetDraggedNodes.map((targetNode) => {
						const currentNode = cursor.draggedNodes?.find(
							(n) => n.id === targetNode.id,
						)
						if (!currentNode) return targetNode

						const nodeDx = targetNode.position.x - currentNode.position.x
						const nodeDy = targetNode.position.y - currentNode.position.y
						const nodeDistance = Math.sqrt(nodeDx * nodeDx + nodeDy * nodeDy)

						if (nodeDistance <= 0.5) return targetNode

						return {
							id: targetNode.id,
							position: {
								x: currentNode.position.x + nodeDx * factor,
								y: currentNode.position.y + nodeDy * factor,
							},
						}
					})
					hasChanges = true
				}

				// Interpolate selection box (x, y, width, height)
				if (cursor.targetSelectionBox && cursor.selectionBox) {
					const selDx = cursor.targetSelectionBox.x - cursor.selectionBox.x
					const selDy = cursor.targetSelectionBox.y - cursor.selectionBox.y
					const selDw =
						cursor.targetSelectionBox.width - cursor.selectionBox.width
					const selDh =
						cursor.targetSelectionBox.height - cursor.selectionBox.height
					const selDistance = Math.sqrt(
						selDx * selDx + selDy * selDy + selDw * selDw + selDh * selDh,
					)

					if (selDistance > 0.5) {
						cursor.selectionBox = {
							...cursor.selectionBox,
							x: cursor.selectionBox.x + selDx * factor,
							y: cursor.selectionBox.y + selDy * factor,
							width: cursor.selectionBox.width + selDw * factor,
							height: cursor.selectionBox.height + selDh * factor,
						}
						hasChanges = true
					}
				}
			}

			if (hasChanges) {
				onUpdate(new Map(cursorsRef.current))
			}

			animationFrameRef.current = requestAnimationFrame(animate)
		}

		animationFrameRef.current = requestAnimationFrame(animate)

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current)
			}
		}
	}, [cursorsRef, onUpdate])

	return { updateCursor, removeCursor }
}
